import GameMap from "../game/gameMap";
import { clamp01, saveJSON } from "../helpers";
import MapEditorScreen from "../mapEditorScreen";
import { NaturalSpawnEntry, WorldDescriptor } from "../types";
import Button from "../ui/button";
import DropDownList from "../ui/dropDownList";
import HorizontalSeparator from "../ui/horizontalSeparator";
import HorizontalView from "../ui/horizontalView";
import Label from "../ui/label";
import TextInput from "../ui/textInput";
import UIElement from "../ui/uiElement";
import UIOverlay from "../ui/uiOverlay";
import VerticalView from "../ui/verticalView";

// Centered panel (like the world select tool) that edits the world
// descriptor (world.json): default tile, liquid and the natural
// objects/entities spawn lists with their spawn chances.
class WorldSettingsTool implements UIElement {
    x = 0;
    y = 0;
    w = 80;
    h = 20;
    action = null;

    PANEL_WIDTH = 250;
    ROW_WIDTH = 240;
    CHANCE_CHARS = "0123456789.";
    SCROLL_STEP = 40;

    //scrolling - when the content is taller than the area between the top
    //menu bar and the bottom of the window, the panel becomes scrollable
    scrollOffset = 0;
    maxScrollOffset = 0;
    contentHeight = 0;

    OBJECT_NAMES = ["brick_block", "grass", "info_sign", "plant_big", "plant_small", "portal", "rock", "spike1", "spike2", "tree1", "tree2", "tree3", "tree4", "tree5", "tree6", "tree7", "dead_tree1", "dead_tree2", "dead_tree3", "tree_glitched", "pumpkin001", "pumpkin002", "pumpkin003", "pumpkin004", "pumpkin005", "pumpkin006", "torch"];
    ENTITY_NAMES = ["testEntity", "chicken", "spider", "chest", "coin", "heart", "egg", "mistery"];

    // ------ UI ------
    ui: VerticalView = new VerticalView(0, 0, 10, 10);

    te_defaultTile: TextInput | null = null;
    drp_liquid: DropDownList | null = null;

    te_addObjectName: TextInput | null = null;
    te_addObjectChance: TextInput | null = null;
    drp_addEntity: DropDownList | null = null;
    te_addEntityChance: TextInput | null = null;

    //chance inputs bound to their descriptor entries, so unconfirmed
    //edits (no Enter pressed) can still be applied before saving
    chanceBindings: {entry: NaturalSpawnEntry, input: TextInput}[] = [];

    //the descriptor instance the UI was built for; used to detect
    //async loads and world switches so the panel rebuilds itself
    builtForData: WorldDescriptor | null = null;

    constructor(private gameMap : GameMap, private editor: MapEditorScreen) {
        this.rebuildUI();
    }

    getData(): WorldDescriptor | null {
        return this.gameMap.worldDefaultData;
    }

    open(){
        this.scrollOffset = 0;
        this.rebuildUI();
    }

    rebuildUI(){
        //the UI elements are recreated - close any popup one of the old
        //elements (a drop down) may still own, so it can't go stale
        UIOverlay.clear();

        let data = this.getData();
        this.builtForData = data;
        this.chanceBindings = [];

        this.ui = new VerticalView(0, 0, 10, 10);
        this.ui.horizontalAlignment = "LEFT";
        this.ui.verticalAlignment = "TOP";

        this.ui.addElement(new Label(`World Settings: ${this.gameMap.worldName}`));

        if(!data){
            this.ui.addElement(new Label("World data is still loading..."));
            this.updateLayout();
            return;
        }

        // ------ Default tile & liquid ------
        let tileRow = new HorizontalView(0, 0, this.ROW_WIDTH, 30);
        tileRow.background = false;
        tileRow.addElement(new Label("Default Tile:"));
        this.te_defaultTile = new TextInput(100, 20, data.defaultTile, "Tile image used by generated chunks");
        this.te_defaultTile.ALLOWED_CHARS = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789_";
        tileRow.addElement(this.te_defaultTile);
        this.ui.addElement(tileRow);

        let liquidRow = new HorizontalView(0, 0, this.ROW_WIDTH, 30);
        liquidRow.background = false;
        liquidRow.addElement(new Label("Liquid:"));
        this.drp_liquid = new DropDownList(100, ["empty", "water", "lava"], "Liquid rendered on generated chunks");
        this.drp_liquid.selectedOption = data.liquid ? data.liquid : "empty";
        liquidRow.addElement(this.drp_liquid);
        this.ui.addElement(liquidRow);

        // ------ Natural objects ------
        this.ui.addElement(new HorizontalSeparator(this.ROW_WIDTH));
        this.ui.addElement(new Label("Natural Objects (chance 0-1 per attempt):"));

        for(let entry of data.naturalObjects){
            this.ui.addElement(this.buildEntryRow(data.naturalObjects, entry));
        }

        let addObjectRow = new HorizontalView(0, 0, this.ROW_WIDTH, 30);
        addObjectRow.background = false;
        this.te_addObjectName = new TextInput(100, 20, "", "Object name to add (e.g. tree1)");
        this.te_addObjectName.ALLOWED_CHARS = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789_";
        addObjectRow.addElement(this.te_addObjectName);
        this.te_addObjectChance = this.buildChanceInput("0.5");
        addObjectRow.addElement(this.te_addObjectChance);
        let btn_addObject = new Button(50, 20, "Add");
        btn_addObject.action = () => {
            let name = this.te_addObjectName ? this.te_addObjectName.text.trim() : "";
            if(name.length === 0){
                return;
            }
            data.naturalObjects.push({
                name: name,
                chance: this.parseChance(this.te_addObjectChance, 0.5)
            });
            this.rebuildUI();
        }
        addObjectRow.addElement(btn_addObject);
        this.ui.addElement(addObjectRow);

        // ------ Natural entities ------
        this.ui.addElement(new HorizontalSeparator(this.ROW_WIDTH));
        this.ui.addElement(new Label("Natural Entities (chance 0-1 per attempt):"));

        for(let entry of data.naturalEntities){
            this.ui.addElement(this.buildEntryRow(data.naturalEntities, entry));
        }

        let addEntityRow = new HorizontalView(0, 0, this.ROW_WIDTH, 30);
        addEntityRow.background = false;
        this.drp_addEntity = new DropDownList(100, this.ENTITY_NAMES, "Entity to add");
        addEntityRow.addElement(this.drp_addEntity);
        this.te_addEntityChance = this.buildChanceInput("0.5");
        addEntityRow.addElement(this.te_addEntityChance);
        let btn_addEntity = new Button(50, 20, "Add");
        btn_addEntity.action = () => {
            if(!this.drp_addEntity){
                return;
            }
            data.naturalEntities.push({
                name: this.drp_addEntity.selectedOption,
                chance: this.parseChance(this.te_addEntityChance, 0.5)
            });
            this.rebuildUI();
        }
        addEntityRow.addElement(btn_addEntity);
        this.ui.addElement(addEntityRow);

        // ------ Save ------
        this.ui.addElement(new HorizontalSeparator(this.ROW_WIDTH));
        let btn_save = new Button(this.ROW_WIDTH - 10, 22, "Save World Settings");
        btn_save.action = () => {
            this.save();
        }
        this.ui.addElement(btn_save);

        this.updateLayout();
    }

    //one row: locked name input | chance input | remove button
    buildEntryRow(list: NaturalSpawnEntry[], entry: NaturalSpawnEntry): HorizontalView {
        let row = new HorizontalView(0, 0, this.ROW_WIDTH, 30);
        row.background = false;

        let nameInput = new TextInput(120, 20, entry.name, "Name");
        nameInput.locked = true;
        row.addElement(nameInput);

        let chanceInput = this.buildChanceInput(entry.chance.toString());
        chanceInput.tooltip = "Spawn chance (0-1)";
        chanceInput.action = () => {
            entry.chance = this.parseChance(chanceInput, entry.chance);
            chanceInput.text = entry.chance.toString();
        }
        this.chanceBindings.push({entry: entry, input: chanceInput});
        row.addElement(chanceInput);

        let btn_remove = new Button(20, 20, "X");
        btn_remove.action = () => {
            let index = list.indexOf(entry);
            if(index > -1){
                list.splice(index, 1);
            }
            this.rebuildUI();
        }
        row.addElement(btn_remove);

        return row;
    }

    buildChanceInput(text: string): TextInput {
        let input = new TextInput(45, 20, text, "Spawn chance (0-1)");
        input.ALLOWED_CHARS = this.CHANCE_CHARS;
        return input;
    }

    parseChance(input: TextInput | null, fallback: number): number {
        if(!input){
            return fallback;
        }
        let parsed = parseFloat(input.text);
        if(isNaN(parsed)){
            return fallback;
        }
        return Math.round(clamp01(parsed) * 1000) / 1000;
    }

    save(){
        let data = this.getData();
        if(!data){
            return;
        }

        if(this.te_defaultTile && this.te_defaultTile.text.trim().length > 0){
            data.defaultTile = this.te_defaultTile.text.trim();
        }
        if(this.drp_liquid){
            data.liquid = this.drp_liquid.selectedOption;
        }

        //apply chance edits that were typed but not confirmed with Enter
        for(let binding of this.chanceBindings){
            binding.entry.chance = this.parseChance(binding.input, binding.entry.chance);
        }

        let path = `/assets/worlds/${this.gameMap.worldName}/world.json`;
        console.log("Saving World Settings : ", path);
        saveJSON(data, path);

        this.rebuildUI();
    }

    updateLayout(): void {
        //size the panel to its content
        let totalHeight = this.ui.spacing;
        for(let element of this.ui.elements){
            totalHeight += element.h + this.ui.spacing;
        }
        this.contentHeight = totalHeight;

        this.ui.w = this.PANEL_WIDTH;
        this.ui.x = -this.ui.w/2;

        if(!buffer){
            //layout can be requested before the render buffer exists -
            //fall back to the old centered layout
            this.ui.h = totalHeight;
            this.ui.y = -this.ui.h/2;
            this.ui.updateLayout();
            return;
        }

        //the panel must stay between the top menu bar and the bottom edge
        //of the window. When the content is taller than that area, it is
        //clipped to it and scrolled with the mouse wheel instead of
        //overflowing under the top bar / outside the window.
        let topLimit = -buffer.height/2 + this.editor.topBarHeight;
        let bottomLimit = buffer.height/2;
        let availableHeight = bottomLimit - topLimit;
        let visibleHeight = Math.min(totalHeight, availableHeight);

        this.maxScrollOffset = Math.max(0, totalHeight - availableHeight);
        this.scrollOffset = Math.min(Math.max(this.scrollOffset, 0), this.maxScrollOffset);

        //center the panel while it fits, pin it right below the top bar
        //once it doesn't
        let panelTop = Math.max(topLimit, -totalHeight/2);

        //lay the content out shifted up by the scroll offset, then restore
        //the panel frame to the visible area only. Content that ends up
        //above the frame is covered by the top menu bar, content below is
        //cut off by the window edge.
        this.ui.y = panelTop - this.scrollOffset;
        this.ui.h = totalHeight;
        this.ui.updateLayout();

        this.ui.y = panelTop;
        this.ui.h = visibleHeight;
    }

    render() {
        //world.json loads async and can be swapped by loading another
        //world - rebuild the panel whenever the descriptor changes
        if(this.builtForData !== this.getData()){
            this.rebuildUI();
        }

        this.ui.render();

        //scrollbar - only when the content doesn't fit the window
        if(this.maxScrollOffset > 0){
            let trackX = this.ui.x + this.ui.w - 4;
            buffer.noStroke();
            buffer.fill(150);
            buffer.rect(trackX, this.ui.y, 3, this.ui.h);

            let thumbHeight = Math.max(20, this.ui.h * this.ui.h / this.contentHeight);
            let thumbY = this.ui.y + (this.ui.h - thumbHeight) * (this.scrollOffset / this.maxScrollOffset);
            buffer.fill(80);
            buffer.rect(trackX, thumbY, 3, thumbHeight);
        }
    }

    renderTooltip(): boolean {
        this.ui.renderTooltip();
        return false;
    }

    mousePressed(): boolean {
        if(this.ui.mousePressed()){
            return true;
        }

        return false;
    }

    mouseReleased(): boolean {
        if(this.ui.mouseReleased()){
            return true;
        }

        return false;
    }

    keyPressed(): boolean {
        if(this.ui.keyPressed()){
            return true;
        }

        return false;
    }

    keyReleased(): boolean {
        if(this.ui.keyReleased()){
            return true;
        }

        return false;
    }

    scroll(event: WheelEvent): boolean {
        //scroll the panel content when it doesn't fit on the screen
        if(this.maxScrollOffset > 0 && this.ui.mouseInArea()){
            this.scrollOffset += event.deltaY > 0 ? this.SCROLL_STEP : -this.SCROLL_STEP;
            this.updateLayout(); //re-layout + clamp
            return true;
        }

        this.ui.scroll(event);

        return true; //swallow the event so the map doesn't zoom underneath
    }
}

export default WorldSettingsTool;
